import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ProvinceUpdateInput } from "@/modules/geo/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import {
  useProvinceQuery,
  useUpdateProvince,
  useZonesQuery,
} from "../api/queries";
import { ProvinceFormTour, useProvinceFormTour } from "../tour";
import { ProvinceForm } from "../ui/ProvinceForm";

export function ProvinceEditPage() {
  const nav = useNavigate({ from: "/app/geo/provinces/$id/edit" });
  const { id } = useParams({ from: "/app/geo/provinces/$id/edit" });
  const { data, ...result } = useProvinceQuery(id);
  const updateProvince = useUpdateProvince(id);
  const { run, handleJoyrideCallback, startTour } = useProvinceFormTour();
  const zonesQuery = useZonesQuery();
  const zones = zonesQuery.data ?? [];

  const province = data;
  const provinceName =
    province && typeof province.name === "object" && province.name !== null
      ? ((province.name as { lo?: string })?.lo ??
        (province.name as { en?: string })?.en ??
        (province as { code?: string })?.code)
      : ((province as { name?: string })?.name ??
        (province as { code?: string })?.code ??
        id ??
        "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແຂວງ", to: "/app/geo/provinces" },
            { label: provinceName },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂແຂວງ{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງແຂວງ.</p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/geo/provinces" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດແຂວງ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!province ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <ProvinceForm
                zones={zones}
                initialValues={{
                  nameLo: province.name.lo ?? "",
                  nameTh: province.name.th ?? "",
                  nameEn: province.name.en ?? "",
                  zoneId: province.zoneId,
                  enabled: province.enabled,
                  sortOrder: province.sortOrder,
                }}
                onSubmit={async (values: ProvinceUpdateInput) => {
                  try {
                    await updateProvince.mutateAsync(values);
                    nav({ to: "/app/geo/provinces" } as any);
                  } catch (error) {
                    // Error handling is done by mutation
                  }
                }}
                submitting={updateProvince.isPending}
              />
            </div>
          )}
        </QueryState>

        <ProvinceFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
